import React, { useEffect, useState } from "react";
import { api } from "../api";
import "./ManageEmployees.css";

const DEFAULT_ROLES = [
  "Admin",
  "Studio Head",
  "Designer",
  "Draftsman",
  "Senior BIM Modeller",
  "3D Visualizer",
  "BIM Modeller",
  "Architect",
  "Civil Engineer",
  "Site Engineer",
  "Interior Designer",
  "Accountant",
  "Accountantsvb",
  "Sales Manager",
  "Graphic Designer",
  "Project Manager",
  "Marketing Manager",
  "HR Executive",
  "HR Head",
  "IT Manager",
  "Data Entry Operator",
  "Client",
];

const COMPANY_OPTIONS = [
  "Sheth Design Consultants LLP",
  "Softvolute Buildcon LLP",
];

const formatDateForInput = (value) => {
  if (!value) return "";
  try {
    return new Date(value).toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const formatDateForDisplay = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleDateString("en-GB");
  } catch {
    return "-";
  }
};

const ManageEmployees = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    mobileNumber: "",
    role: "Designer",
    hourlyRate: "",
    companyName: "Sheth Design Consultants LLP",
    dateOfBirth: "",
    joiningDate: "",
  });

  const [employees, setEmployees] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [search, setSearch] = useState("");
  const [roles, setRoles] = useState(DEFAULT_ROLES);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [roleSaving, setRoleSaving] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchRoles();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await api.get("/users", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setEmployees(res.data || []);
    } catch {
      alert("Failed to fetch employees");
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await api.get("/roles", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const apiRoles = (res.data || [])
        .map((item) => String(item?.roleName || "").trim())
        .filter(Boolean);

      const mergedRoles = [...DEFAULT_ROLES, ...apiRoles];

      const uniqueRoles = Array.from(
        new Map(
          mergedRoles.map((role) => [
            String(role).trim().toLowerCase(),
            String(role).trim(),
          ]),
        ).values(),
      );

      setRoles(uniqueRoles);

      return uniqueRoles;
    } catch (err) {
      console.error("Failed to load role master:", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });

      // IMPORTANT:
      // Existing local roles ko erase mat karo if GET /roles temporarily fails.
      setRoles((prev) => (prev?.length ? prev : DEFAULT_ROLES));

      return null;
    }
  };

  const handleCreateRole = async () => {
    const roleName = newRoleName.trim();

    if (!roleName) {
      alert("Please enter role name");
      return;
    }

    if (
      roles.some(
        (role) => String(role).trim().toLowerCase() === roleName.toLowerCase(),
      )
    ) {
      alert("This role already exists");
      return;
    }

    try {
      setRoleSaving(true);

      const res = await api.post(
        "/roles",
        {
          roleName,
          type: "Saved",
          permissions: {},
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      const createdRole = String(res?.data?.roleName || roleName).trim();

      // 1) Immediately local dropdown me show karo.
      setRoles((prev) => {
        const merged = [...prev, createdRole];

        return Array.from(
          new Map(
            merged.map((role) => [
              String(role).trim().toLowerCase(),
              String(role).trim(),
            ]),
          ).values(),
        );
      });

      // 2) Newly created role ko automatically select karo.
      setForm((prev) => ({
        ...prev,
        role: createdRole,
      }));

      // 3) Backend se latest master list sync karo.
      // If GET fails, local role list erase nahi hogi.
      await fetchRoles();

      setNewRoleName("");
      setShowRoleModal(false);

      alert(`${createdRole} role created successfully`);
    } catch (err) {
      console.error("Create role error:", {
        status: err?.response?.status,
        data: err?.response?.data,
        message: err?.message,
      });

      alert(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to create role",
      );
    } finally {
      setRoleSaving(false);
    }
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleEditChange = (e) => {
    setEditForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const filteredEmployees = employees.filter((emp) => {
    const text = `${emp.name || ""} ${emp.email || ""} ${emp.role || ""} ${
      emp.mobileNumber || ""
    }`.toLowerCase();

    return text.includes(search.toLowerCase());
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !form.name ||
      !form.email ||
      !form.password ||
      !form.mobileNumber ||
      !form.hourlyRate ||
      !form.companyName ||
      !form.role
    ) {
      alert("All required fields must be filled");
      return;
    }

    try {
      await api.post(
        "/auth/register",
        {
          ...form,
          hourlyRate: parseFloat(form.hourlyRate),
          dateOfBirth: form.dateOfBirth || null,
          joiningDate: form.joiningDate || null,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      setForm({
        name: "",
        email: "",
        password: "",
        mobileNumber: "",
        role: "Designer",
        hourlyRate: "",
        companyName: "Sheth Design Consultants LLP",
        dateOfBirth: "",
        joiningDate: "",
      });

      fetchEmployees();
    } catch (err) {
      alert(err.response?.data?.message || "Error adding user");
    }
  };

  const handleEdit = (emp) => {
    setEditingId(emp._id);
    setEditForm({
      ...emp,
      hourlyRate: emp.hourlyRate ?? "",
      companyName: emp.companyName ?? COMPANY_OPTIONS[0],
      role: emp.role ?? "Designer",
      dateOfBirth: formatDateForInput(emp.dateOfBirth),
      joiningDate: formatDateForInput(emp.joiningDate),
    });
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(
        `/users/${editingId}`,
        {
          ...editForm,
          hourlyRate:
            editForm.hourlyRate === "" ? 0 : parseFloat(editForm.hourlyRate),
          dateOfBirth: editForm.dateOfBirth || null,
          joiningDate: editForm.joiningDate || null,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      setEditingId(null);
      setEditForm({});
      fetchEmployees();
    } catch (err) {
      alert(err.response?.data?.message || "Error updating user");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await api.delete(`/users/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchEmployees();
    } catch {
      alert("Error deleting user");
    }
  };

  return (
    <div className="employees-page">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 16,
        }}
      >
        <h2 className="page-title" style={{ margin: 0 }}>
          👥 Manage Employees / Team Members
        </h2>
        <button
          type="button"
          className="btn"
          onClick={() => setShowRoleModal(true)}
          style={{
            background: "#111a3d",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "10px 15px",
            fontWeight: 700,
          }}
        >
          + Add Role
        </button>
      </div>

      {showRoleModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(15,23,42,.48)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !roleSaving)
              setShowRoleModal(false);
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 430,
              background: "#fff",
              borderRadius: 16,
              boxShadow: "0 24px 70px rgba(15,23,42,.25)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "20px 22px 14px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <div
                  style={{ fontSize: 18, fontWeight: 800, color: "#111a3d" }}
                >
                  Role Master
                </div>
                <div style={{ marginTop: 4, fontSize: 12.5, color: "#64748b" }}>
                  Create a new employee role
                </div>
              </div>
              <button
                type="button"
                onClick={() => !roleSaving && setShowRoleModal(false)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: "50%",
                  border: "1px solid #dbe2ea",
                  background: "#fff",
                  cursor: "pointer",
                  fontSize: 20,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: 22 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 12,
                  fontWeight: 800,
                  color: "#334155",
                  marginBottom: 7,
                }}
              >
                Role Name
              </label>
              <input
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreateRole();
                  }
                }}
                placeholder="e.g. BIM Coordinator"
                autoFocus
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  height: 44,
                  border: "1px solid #cbd5e1",
                  borderRadius: 9,
                  padding: "0 12px",
                  fontSize: 14,
                }}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 9,
                  marginTop: 18,
                }}
              >
                <button
                  type="button"
                  disabled={roleSaving}
                  onClick={() => setShowRoleModal(false)}
                  style={{
                    minWidth: 90,
                    height: 40,
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    background: "#fff",
                    fontWeight: 700,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={roleSaving}
                  onClick={handleCreateRole}
                  style={{
                    minWidth: 110,
                    height: 40,
                    borderRadius: 8,
                    border: "none",
                    background: "#111a3d",
                    color: "#fff",
                    fontWeight: 800,
                    opacity: roleSaving ? 0.7 : 1,
                  }}
                >
                  {roleSaving ? "Saving..." : "Create Role"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="employee-form">
        <FormInput
          label="Employee Name"
          name="name"
          value={form.name}
          onChange={handleChange}
        />
        <FormInput
          label="Email"
          name="email"
          type="email"
          value={form.email}
          onChange={handleChange}
        />
        <FormInput
          label="Password"
          name="password"
          type="password"
          value={form.password}
          onChange={handleChange}
        />
        <FormInput
          label="Mobile Number"
          name="mobileNumber"
          value={form.mobileNumber}
          onChange={handleChange}
        />
        <FormInput
          label="🎂 Birth Date"
          name="dateOfBirth"
          type="date"
          value={form.dateOfBirth}
          onChange={handleChange}
        />
        <FormInput
          label="🏢 Joining Date"
          name="joiningDate"
          type="date"
          value={form.joiningDate}
          onChange={handleChange}
        />
        <FormInput
          label="Hourly Rate"
          name="hourlyRate"
          type="number"
          value={form.hourlyRate}
          onChange={handleChange}
        />

        <div className="form-field">
          <label>Company</label>
          <select
            name="companyName"
            value={form.companyName}
            onChange={handleChange}
          >
            <option value="">Select Company</option>
            {COMPANY_OPTIONS.map((company) => (
              <option key={company} value={company}>
                {company}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label>Role</label>
          <select name="role" value={form.role} onChange={handleChange}>
            <option value="">Select Role</option>
            {roles.map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
        </div>

        <button type="submit" className="btn add-btn">
          ➕ Add User
        </button>
      </form>

      <input
        className="search-input"
        placeholder="🔍 Search employee..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      <div className="table-wrapper">
        <table className="employees-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Mobile</th>
              <th>DOB</th>
              <th>Joining Date</th>
              <th>Hourly Rate</th>
              <th>Company</th>
              <th>Role</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredEmployees.map((emp) => (
              <tr key={emp._id}>
                {editingId === emp._id ? (
                  <>
                    <td>
                      <input
                        name="name"
                        value={editForm.name || ""}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <input
                        name="email"
                        value={editForm.email || ""}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <input
                        name="mobileNumber"
                        value={editForm.mobileNumber || ""}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <input
                        name="dateOfBirth"
                        type="date"
                        value={editForm.dateOfBirth || ""}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <input
                        name="joiningDate"
                        type="date"
                        value={editForm.joiningDate || ""}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <input
                        name="hourlyRate"
                        type="number"
                        value={editForm.hourlyRate ?? ""}
                        onChange={handleEditChange}
                      />
                    </td>
                    <td>
                      <select
                        name="companyName"
                        value={editForm.companyName || ""}
                        onChange={handleEditChange}
                      >
                        {COMPANY_OPTIONS.map((company) => (
                          <option key={company} value={company}>
                            {company}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        name="role"
                        value={editForm.role || ""}
                        onChange={handleEditChange}
                      >
                        {roles.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button onClick={handleSaveEdit} className="btn">
                        💾 Save
                      </button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="btn cancel-btn"
                      >
                        Cancel
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{emp.name}</td>
                    <td>{emp.email}</td>
                    <td>{emp.mobileNumber}</td>
                    <td>{formatDateForDisplay(emp.dateOfBirth)}</td>
                    <td>{formatDateForDisplay(emp.joiningDate)}</td>
                    <td>₹ {emp.hourlyRate}</td>
                    <td>{emp.companyName}</td>
                    <td>{emp.role}</td>
                    <td>
                      <button onClick={() => handleEdit(emp)} className="btn">
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleDelete(emp._id)}
                        className="btn delete-btn"
                      >
                        🗑 Delete
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FormInput = ({ label, name, value, onChange, type = "text" }) => (
  <div className="form-field">
    <label>{label}</label>
    <input name={name} value={value} type={type} onChange={onChange} />
  </div>
);

export default ManageEmployees;
